import { inject, Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { Platform } from '@ionic/angular';
import { AnalyticsBrowser, EventProperties } from '@segment/analytics-next';

import { environment } from '@ninety/mobile/platform/environments';

import { DatadogLogger } from '../datadog/datadog-logger';

import { SegmentContactTraits } from './models/segment-contact-traits';
import { SegmentGroupTraits, SegmentReservedGroupTraits } from './models/segment-group-traits';

@Injectable({
  providedIn: 'root',
})
export class SegmentService {
  private platform = inject(Platform);
  private logger = inject(DatadogLogger);

  private analytics = AnalyticsBrowser.load(
    { writeKey: environment.segmentApiKey },
    {
      integrations: {
        Intercom: false,
        'Intercom Web (Actions)': false,
        'Fullstory (Actions)': false,
      },
    }
  );
  private isNative = this.platform.is('capacitor');

  constructor() {
    if (this.isNative) {
      this.setupNetworkListener();
    }
  }

  page(): void {
    try {
      this.analytics.page();
    } catch (error) {
      this.logger.error({ message: '[Segment] analytics.page error', error });
    }
  }

  screen(screenName: string): void {
    try {
      this.analytics.screen({
        name: screenName,
      });
    } catch (error) {
      this.logger.error({ message: '[Segment] analytics.screen error', error });
    }
  }

  private setupNetworkListener() {
    if (!this.isNative) return;

    Network.addListener('networkStatusChange', status => {
      this.analytics.track('Network Status Changed', {
        connected: status.connected,
        connectionType: status.connectionType,
      });
    });
  }

  /**
   * Allows Segment to tie a user to their actions and record traits about
   * them. It includes a unique User ID and any optional traits you know
   * about the user, like their email, name, and more.
   *
   * Reference:
   * https://segment.com/docs/connections/spec/identify/
   */
  async identify(contact: SegmentContactTraits): Promise<void> {
    try {
      if (!this.platform.is('capacitor')) {
        this.analytics.identify(contact.id, contact);
      } else {
        const deviceInfo = await Device.getInfo();
        const osInfo = deviceInfo.operatingSystem;
        this.analytics.identify(contact.id, contact, {
          context: {
            device: {
              type: deviceInfo.model,
              manufacturer: deviceInfo.manufacturer,
              model: deviceInfo.model,
              name: deviceInfo.name,
            },
            os: {
              name: osInfo,
            },
            app: {
              name: 'Ninety',
              version: '19', // Get from package.json
            },
          },
        });
      }
    } catch (e) {
      this.logger.error({
        message: '[Segment] Error creating contact association',
        error: e,
        data: { contactId: contact?.id },
      });
    }
  }

  group(group: SegmentReservedGroupTraits | SegmentGroupTraits): void {
    try {
      this.analytics.group(group.id, group);
    } catch (e) {
      this.logger.error({
        message: '[Segment] Error creating group association',
        error: e,
        data: { groupId: group?.id },
      });
    }
  }

  /**
   * Reference:
   * https://segment.com/docs/connections/spec/track/#properties
   */
  track<T extends EventProperties>(event: string, fields?: T) {
    try {
      this.analytics.track(event, fields);
    } catch (error) {
      this.logger.error({ message: '[Segment] Error tracking event', error, data: { event } });
    }
  }
}
